import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import Loader from "../../components/Loader/Loader";
import { getColours, createColour } from "../../utilities/colours-service";
import { getThemes, createTheme } from "../../utilities/themes-service";
import { createStamp } from "../../utilities/stamps-service";
import ColourItem from "./ColourItem";
import ThemeItem from "./ThemeItem";
import ImageForm from "../../components/ImageForm/ImageForm";

export default function StampsNewPage({ user }) {
  const navigate = useNavigate();

  // image form states
  const [imageFormData, setImageFormData] = useState();
  const [imageUrl, setImageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [colours, setColours] = useState([]);
  const [themes, setThemes] = useState([]);
  const [formData, setFormData] = useState({
    catalogue_id: "",
    catalogNumbers: "",
    subject: "",
    year: "",
    denomination: "",
    condition: "",
    yearPostmarked: "",
    description: "",
    colours: [],
    themes: [],
    hidden: false,
  });
  const [colourFormData, setColourFormData] = useState("");
  const [themeFormData, setThemeFormData] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  // image form states

  async function handleRequest() {
    const coloursResponse = await getColours();
    if (typeof coloursResponse === "object") {
      setColours(coloursResponse);
    } else {
      console.log("error: ", coloursResponse);
    }
    const themesResponse = await getThemes();
    if (typeof themesResponse === "object") {
      setThemes(themesResponse);
    } else {
      console.log("error: ", themesResponse);
    }
    setIsLoading(false);
  }

  const catalogueIdHasSpaces = /\s/.test(formData.catalogue_id);

  async function handleSubmit(evt) {
    evt.preventDefault();
    // Validate here — the inputs' required attributes never fire because
    // they aren't inside a <form>
    if (!formData.catalogue_id.trim()) {
      setError("Catalogue ID is required.");
      return;
    }
    if (catalogueIdHasSpaces) {
      setError("Catalogue ID must not contain spaces.");
      return;
    }
    if (!formData.description.trim()) {
      setError("Description is required.");
      return;
    }
    try {
      setIsSubmitting(true);
      setError("");
      const finalFormData = { ...formData };
      finalFormData.image = imageUrl;
      const result = await createStamp(finalFormData);
      if (result instanceof Error) {
        if (result.status === 409) {
          setError(
            "This Catalogue ID is already in use. Please choose a different one.",
          );
        } else {
          setError("Something went wrong. Please try again.");
        }
        setIsSubmitting(false);
        return;
      }
      navigate("/");
    } catch (err) {
      console.log(err);
      setError("Something went wrong. Please try again.");
      setIsSubmitting(false);
    }
  }

  function handleChange(evt) {
    const newFormData = { ...formData, [evt.target.name]: evt.target.value };
    setFormData(newFormData);
  }

  async function handleAddColour(evt) {
    evt.preventDefault();
    const colourData = { colourName: colourFormData.trim().toLowerCase() };
    try {
      const newColour = await createColour(colourData);
      if (newColour instanceof Error || !newColour?._id) {
        console.log("error creating colour:", newColour);
        return;
      }
      await handleRequest();
      const newFormData = { ...formData };
      newFormData.colours.push(newColour._id);
      setFormData(newFormData);
      setColourFormData("");
    } catch (error) {
      console.log(error);
    }
  }

  async function handleAddTheme(evt) {
    evt.preventDefault();
    const themeData = { themeName: themeFormData.trim().toLowerCase() };
    try {
      const newTheme = await createTheme(themeData);
      if (newTheme instanceof Error || !newTheme?._id) {
        console.log("error creating theme:", newTheme);
        return;
      }
      await handleRequest();
      const newFormData = { ...formData };
      newFormData.themes.push(newTheme._id);
      setFormData(newFormData);
      setThemeFormData("");
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    handleRequest();
  }, []);

  // Plain case-insensitive substring match — user input in a RegExp would
  // crash on characters like ( or [
  const colourQuery = colourFormData.trim().toLowerCase();
  const themeQuery = themeFormData.trim().toLowerCase();

  const filteredColours = colours?.filter((colour) =>
    colour.colourName.toLowerCase().includes(colourQuery),
  );

  const filteredThemes = themes?.filter((theme) =>
    theme.themeName.toLowerCase().includes(themeQuery),
  );

  const filteredColoursList = filteredColours?.map((colour, idx) => (
    <ColourItem
      key={colour._id}
      colour={colour}
      idx={idx}
      formData={formData}
      setFormData={setFormData}
      setColourFormData={setColourFormData}
    />
  ));
  const allColourNames = colours?.map((colour) =>
    colour.colourName.toLowerCase(),
  );

  const filteredThemesList = filteredThemes?.map((theme, idx) => (
    <ThemeItem
      key={theme._id}
      theme={theme}
      idx={idx}
      formData={formData}
      setFormData={setFormData}
      setThemeFormData={setThemeFormData}
    />
  ));
  const allThemeNames = themes?.map((theme) => theme.themeName.toLowerCase());

  return !user?.isAdmin ? (
    <div className="text-center py-20">
      <p className="text-gray-500 mb-4">Not authorised</p>
      <button
        className="text-sm underline text-gray-900"
        onClick={() => navigate("/")}
      >
        Return to home
      </button>
    </div>
  ) : (
    <div className="max-w-2xl mx-auto py-10 px-4">
      <h1 className="text-2xl font-semibold tracking-tight text-gray-900 mb-8">
        New stamp
      </h1>
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
        <div className="space-y-5">
          {/* upload image */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">
              Image <span className="text-red-500 font-normal">(Required)</span>
            </p>
            <div>
              <ImageForm
                imageFormData={imageFormData}
                setImageFormData={setImageFormData}
                imageUrl={imageUrl}
                setImageUrl={setImageUrl}
              />
            </div>
          </div>

          {/* catalogue ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-0.5">
              Catalogue ID{" "}
              <span className="text-red-500 font-normal">(Required)</span>
            </label>
            <p className="text-xs text-gray-400 mb-1">
              This is what appears in the URL (e.g., /stamps/773). It must not
              contain spaces.
            </p>
            <input
              type="text"
              name="catalogue_id"
              value={formData.catalogue_id}
              onChange={handleChange}
              required
              placeholder="Unique catalogue identifier"
              className={`w-full border-b bg-transparent py-2 text-sm text-gray-900 outline-none transition-colors focus:border-gray-900 placeholder:text-gray-400 ${catalogueIdHasSpaces ? "border-red-400" : "border-gray-300"}`}
            />
            {catalogueIdHasSpaces && (
              <p className="text-xs text-red-600 mt-1">
                Catalogue ID must not contain spaces.
              </p>
            )}
          </div>

          {/* catalog numbers */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Catalog numbers
            </label>
            <input
              type="text"
              name="catalogNumbers"
              value={formData.catalogNumbers}
              onChange={handleChange}
              className="w-full border-b border-gray-300 bg-transparent py-2 text-sm text-gray-900 outline-none transition-colors focus:border-gray-900 placeholder:text-gray-400"
            />
          </div>

          {/* subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subject
            </label>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              className="w-full border-b border-gray-300 bg-transparent py-2 text-sm text-gray-900 outline-none transition-colors focus:border-gray-900 placeholder:text-gray-400"
            />
          </div>

          {/* year of first issue */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Year of first issue
            </label>
            <input
              type="text"
              name="year"
              value={formData.year}
              onChange={handleChange}
              placeholder="e.g. 1965 or unknown"
              className="w-full border-b border-gray-300 bg-transparent py-2 text-sm text-gray-900 outline-none transition-colors focus:border-gray-900 placeholder:text-gray-400"
            />
          </div>

          {/* denomination */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Denomination
            </label>
            <input
              type="text"
              name="denomination"
              value={formData.denomination}
              onChange={handleChange}
              className="w-full border-b border-gray-300 bg-transparent py-2 text-sm text-gray-900 outline-none transition-colors focus:border-gray-900 placeholder:text-gray-400"
            />
          </div>

          {/* themes */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Themes</p>
            <form onSubmit={handleAddTheme}>
              <div className="flex gap-2 items-center">
                <input
                  required
                  placeholder="Search theme"
                  type="text"
                  name="themeName"
                  value={themeFormData}
                  onChange={(evt) => setThemeFormData(evt.target.value)}
                  className="flex-1 border-b border-gray-300 bg-transparent py-2 text-sm outline-none focus:border-gray-900 placeholder:text-gray-400"
                />
                <button
                  type="submit"
                  disabled={allThemeNames.includes(themeQuery)}
                  className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 hover:border-gray-900 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Add theme
                </button>
              </div>
            </form>
            {isLoading ? (
              <Loader />
            ) : (
              <div className="flex flex-wrap gap-2 mt-2">
                {filteredThemesList}
              </div>
            )}
          </div>

          {/* colours */}
          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Colours</p>
            <form onSubmit={handleAddColour}>
              <div className="flex gap-2 items-center">
                <input
                  required
                  placeholder="Search colour"
                  type="text"
                  name="colourName"
                  value={colourFormData}
                  onChange={(evt) => setColourFormData(evt.target.value)}
                  className="flex-1 border-b border-gray-300 bg-transparent py-2 text-sm outline-none focus:border-gray-900 placeholder:text-gray-400"
                />
                <button
                  type="submit"
                  disabled={allColourNames.includes(colourQuery)}
                  className="text-sm px-3 py-1.5 rounded-lg border border-gray-300 hover:border-gray-900 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Add colour
                </button>
              </div>
            </form>
            {isLoading ? (
              <Loader />
            ) : (
              <div className="flex flex-wrap gap-2 mt-2">
                {filteredColoursList}
              </div>
            )}
          </div>

          {/* condition */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Condition
            </label>
            <select
              name="condition"
              value={formData.condition}
              onChange={handleChange}
              className="w-full border-b border-gray-300 bg-transparent py-2 text-sm text-gray-900 outline-none transition-colors focus:border-gray-900"
            >
              <option value="">Select condition</option>
              <option value="Unused">Unused</option>
              <option value="Postmarked">Postmarked</option>
            </select>
          </div>

          {/* year postmarked — only shown when condition is postmarked */}
          {formData.condition === "Postmarked" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Year postmarked
              </label>
              <input
                type="text"
                name="yearPostmarked"
                value={formData.yearPostmarked}
                onChange={handleChange}
                placeholder="e.g. 1968 or unknown"
                className="w-full border-b border-gray-300 bg-transparent py-2 text-sm text-gray-900 outline-none transition-colors focus:border-gray-900 placeholder:text-gray-400"
              />
            </div>
          )}

          {/* description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description{" "}
              <span className="text-red-500 font-normal">(Required)</span>
            </label>
            <textarea
              name="description"
              rows={6}
              value={formData.description}
              onChange={handleChange}
              required
              className="w-full min-h-[150px] border border-gray-300 rounded-lg bg-transparent p-3 text-sm text-gray-900 outline-none transition-colors focus:border-gray-900 placeholder:text-gray-400 resize-y"
            ></textarea>
            <p className="text-xs text-gray-400 mt-1">
              Supports formatting: **bold**, *italic*, [link text](url)
            </p>
          </div>

          {/* hidden */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="hidden"
              name="hidden"
              checked={formData.hidden}
              onChange={(evt) =>
                setFormData({ ...formData, hidden: evt.target.checked })
              }
              className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
            />
            <label
              htmlFor="hidden"
              className="text-sm font-medium text-gray-700"
            >
              Hidden
            </label>
          </div>

          {/* error message */}
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* submit button */}
          <button
            onClick={handleSubmit}
            disabled={!imageUrl || isSubmitting || catalogueIdHasSpaces}
            className="w-full mt-6 rounded-lg bg-gray-900 py-3 text-sm font-medium text-white transition-colors hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? <Loader /> : "Add stamp"}
          </button>
        </div>
      </div>
    </div>
  );
}
